import { beginGrantTiming, recordGeoSuccess, clearSilentGrant } from "./geoEvidence";

// One GPS fix from the browser, as a promise that never rejects:
//   { lat, lng, accuracy }                         — a fix (accuracy in meters)
//   { error: 'denied' | 'unavailable' | 'timeout' | 'unsupported' }
// HTTPS-only API; in the Capacitor app the WKWebView serves it once the native
// build carries the location permission strings. Shared by Marketing Hub
// check-ins (useGeolocation) and clock-ins (utils/clockGps.js).
//
// `retry`: after a time-out or "unavailable", one longer cache-free try — the
// iPad's FIRST fix after granting permission often fails while the GPS warms
// up. A denial is never retried (iOS won't re-prompt; it reads as a hang).
export function locateOnce({ timeout = 12000, maximumAge = 30000, retry = true, retryTimeout = 15000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      resolve({ error: "unsupported" });
      return;
    }
    const endTiming = beginGrantTiming(); // silent-grant vs popup evidence
    const succeed = (pos) => {
      endTiming();
      recordGeoSuccess();
      resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) });
    };
    const fail = (err) => {
      endTiming();
      if (err.code === 1) clearSilentGrant();
      resolve({ error: err.code === 1 ? "denied" : err.code === 2 ? "unavailable" : "timeout" });
    };
    navigator.geolocation.getCurrentPosition(
      succeed,
      (err) => {
        if (err.code === 1 || !retry) {
          fail(err);
          return;
        }
        navigator.geolocation.getCurrentPosition(succeed, fail, { enableHighAccuracy: true, timeout: retryTimeout, maximumAge: 0 });
      },
      { enableHighAccuracy: true, timeout, maximumAge },
    );
  });
}
